import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchGameLibrarySummary, type GameLibrarySummary } from "@/features/games/games.api";
import { readPageState, writePageState } from "@/features/preferences/page-state";
import { useUserSettingsStore } from "@/features/preferences/user-settings.store";
import {
  ALL_FORMATS,
  clockOptions,
  formatOptions,
  initialPerformanceSelection,
  queryForSelection,
  transitionPerformanceSelection,
  type PerformanceFormatSelection,
  type PerformanceCountMode,
  type PerformanceSelectionState,
  type PerformanceSelectionStep,
} from "./performance-selection";

export const usePerformanceSelection = (
  preferredFormat?: string | null,
  {
    includeAllFormats = false,
    countMode = "analyzed",
    page,
  }: { includeAllFormats?: boolean; countMode?: PerformanceCountMode; page?: "patterns" | "trends" } = {},
) => {
  const owner = useUserSettingsStore((state) => state.ownerUserId);
  const settings = useUserSettingsStore((state) => state.settings);
  const defaultFormat = page === "patterns" ? settings.patternsDefaultFormat : settings.trendsDefaultFormat;
  const defaultClock = page === "patterns" ? settings.patternsDefaultClock : settings.trendsDefaultClock;
  const [summary, setSummary] = useState<GameLibrarySummary | null>(null);
  const [selection, setSelection] = useState<PerformanceSelectionState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    void fetchGameLibrarySummary(controller.signal)
      .then((next) => {
        if (controller.signal.aborted) return;
        setSummary(next);
        let initial = initialPerformanceSelection(next, preferredFormat ?? (page ? defaultFormat : null), countMode);
        const saved =
          page && !preferredFormat ? readPageState<PerformanceSelectionState>(`${page}:selection`) : undefined;
        if (saved && formatOptions(next, includeAllFormats, countMode).some(({ value }) => value === saved.format)) {
          const clocks =
            saved.format && saved.format !== ALL_FORMATS ? clockOptions(next, saved.format, countMode) : [];
          if (
            (saved.clock === null || clocks.some(({ value }) => value === saved.clock)) &&
            ["format", "clock", "results"].includes(saved.step)
          )
            initial = saved;
        } else if (
          initial &&
          page &&
          !preferredFormat &&
          defaultClock &&
          initial.format &&
          initial.format !== ALL_FORMATS &&
          clockOptions(next, initial.format, countMode).some(({ value }) => value === defaultClock)
        ) {
          initial = transitionPerformanceSelection(
            initial,
            { type: "clock", clock: defaultClock },
            next,
            includeAllFormats,
            countMode,
          );
        }
        setSelection(initial);
      })
      .catch((reason: unknown) => {
        if (!controller.signal.aborted)
          setError(reason instanceof Error ? reason.message : "Games could not be loaded");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [
    revision,
    countMode,
    preferredFormat,
    page,
    owner,
    defaultFormat,
    defaultClock,
    includeAllFormats,
    settings.rememberPageState,
  ]);

  const transition = useCallback(
    (action: Parameters<typeof transitionPerformanceSelection>[1]) => {
      if (summary)
        setSelection((current) => {
          const next = current
            ? transitionPerformanceSelection(current, action, summary, includeAllFormats, countMode)
            : current;
          if (page && next) writePageState(`${page}:selection`, next);
          return next;
        });
    },
    [countMode, includeAllFormats, summary, page],
  );
  const formats = useMemo(
    () => (summary ? formatOptions(summary, includeAllFormats, countMode) : []),
    [countMode, includeAllFormats, summary],
  );
  const clocks = useMemo(
    () =>
      summary && selection?.format && selection.format !== ALL_FORMATS
        ? clockOptions(summary, selection.format, countMode)
        : [],
    [countMode, selection, summary],
  );
  const selectedFormats = useMemo(
    () =>
      selection?.format === ALL_FORMATS
        ? formats.flatMap(({ value }) => (value === ALL_FORMATS ? [] : [value]))
        : selection?.format
          ? [selection.format]
          : [],
    [formats, selection],
  );

  return {
    refresh: () => setRevision((value) => value + 1),
    summary,
    selection,
    formats,
    clocks,
    selectedFormats,
    query: selection ? queryForSelection(selection) : null,
    loading,
    error,
    empty: !loading && !error && selection == null,
    showResults: selection?.step === "results",
    edit: (step: PerformanceSelectionStep) => transition({ type: "edit", step }),
    selectFormat: (format: PerformanceFormatSelection) => transition({ type: "format", format }),
    selectClock: (clock: string) => transition({ type: "clock", clock }),
  };
};
